<main>
  <loading-overlay [isLoading]="isLoading() > 0" [fullWidth]="true" />
  <p-toast position="bottom-right" key="br" />

  <header>
    <div class="headline">
      <h1>{{ headerTitle() }}</h1>
      @if (!isNew()) {
        <p-tag [severity]="statusSeverity(status())" [value]="statusLabel(status())" />
      }
    </div>
    <div class="buttons">
      @if (isEditable()) {
        <p-button [label]="'Delete' | translateWF: 'Delete'" icon="pi pi-trash" severity="danger" variant="outlined" (onClick)="confirmDelete()" />
        <p-button [label]="'ExpenseSettlement_Post' | translateWF: 'Post'" icon="pi pi-book" severity="success" (onClick)="confirmPost()" />
        <span class="separator"></span>
      }
      @if (canSettle && !isPosted()) {
        <p-splitbutton
          [label]="isNew() ? ('ExpenseSettlement_Create' | translateWF: 'Create settlement') : ('Save' | translateWF: 'Save')"
          (onClick)="save(false)"
          [model]="saveMenuItems"
        />
      }
      <p-button variant="outlined" (onClick)="back()">{{ 'Back' | translateWF: 'Back' }}</p-button>
    </div>
  </header>

  <div class="content">
    <p-confirmDialog />

    @if (isNew()) {
      <div class="grid-12">
        <div class="span-12-12-8">
          <p-panel [header]="'ExpenseSettlement_Section_Owner' | translateWF: 'What to settle'">
            <p-card>
              <div class="flex flex-col gap-4">
                <div class="form-box">
                  <label for="settlement-owner">{{ 'ExpenseSettlement_Field_Owner' | translateWF: 'Fund or trip' }} *</label>
                  <p-select
                    inputId="settlement-owner"
                    [formControl]="ownerControl"
                    [options]="ownerOptions()"
                    optionLabel="label"
                    optionValue="value"
                    [filter]="true"
                    [placeholder]="'ExpenseSettlement_Owner_Placeholder' | translateWF: 'Select a fund or trip'"
                    [emptyMessage]="'ExpenseSettlement_Owner_Empty' | translateWF: 'No fund or trip has posted claims to settle.'"
                    styleClass="w-full"
                    [ngClass]="{ 'ng-invalid ng-dirty': ownerControl.touched && ownerControl.invalid }"
                  />
                  <small class="text-surface-500">
                    {{
                      'ExpenseSettlement_Owner_Hint'
                        | translateWF
                          : 'Funds and trips with posted claims still to pay, most outstanding first. Creating the settlement ticks all of them; untick what this payment does not cover.'
                    }}
                  </small>
                </div>
              </div>
            </p-card>
          </p-panel>
        </div>
      </div>
    }
    @if (isNew() ? null : settlement(); as current) {
      <div class="grid-12">
        <div class="span-12-12-8 flex flex-col gap-5">
          <p-panel [header]="'ExpenseSettlement_Section_Data' | translateWF: 'Settlement'">
            <p-card>
              <form [formGroup]="form" class="grid grid-cols-12 gap-5">
                <div class="form-box col-span-12 md:col-span-8">
                  <label>{{ 'ExpenseSettlement_Field_Owner' | translateWF: 'Fund or trip' }}</label>
                  <span class="flex items-center gap-2 py-2">
                    <i [class]="isTrip() ? 'pi pi-briefcase' : 'pi pi-wallet'" class="text-surface-500" aria-hidden="true"></i>
                    <a class="cs-table-link" (click)="openOwner()">{{ current.ownerName }}</a>
                    <small class="text-surface-500 font-mono">{{ current.owner.number }}</small>
                  </span>
                </div>
                <div class="form-box col-span-12 md:col-span-4">
                  <label for="settlement-date">{{ fieldLabels.settlementDate | translateWF: 'Date' }} *</label>
                  <p-datepicker
                    inputId="settlement-date"
                    formControlName="settlementDate"
                    dateFormat="dd/mm/yy"
                    [showIcon]="true"
                    appendTo="body"
                    styleClass="w-full"
                    [ngClass]="{ 'ng-invalid ng-dirty': form.controls.settlementDate.invalid && form.controls.settlementDate.touched }"
                  />
                </div>
                <div class="form-box col-span-12">
                  <label for="settlement-note">{{ fieldLabels.note | translateWF: 'Note' }}</label>
                  <textarea id="settlement-note" pTextarea class="w-full" rows="2" formControlName="note" maxlength="500"></textarea>
                </div>
              </form>
            </p-card>
          </p-panel>

          <p-panel>
            <ng-template pTemplate="header">
              <span class="font-semibold">
                {{ 'ExpenseSettlement_Section_Claims' | translateWF: 'Claims to settle' }}
                <p-tag class="ml-2" severity="secondary" [value]="selectedClaims() + ' / ' + claimRows.length" />
              </span>
            </ng-template>
            @if (isEditable() && claimRows.length > 1) {
              <ng-template pTemplate="icons">
                <p-button [label]="'ExpenseSettlement_SelectAll' | translateWF: 'Select all'" size="small" variant="text" (onClick)="toggleAll(claimRows, true)" />
                <p-button [label]="'ExpenseSettlement_SelectNone' | translateWF: 'Clear'" size="small" variant="text" (onClick)="toggleAll(claimRows, false)" />
              </ng-template>
            }
            <p-card data-variant="compact">
              <p-table [value]="claimRows.controls" [rowHover]="true">
                <ng-template pTemplate="header">
                  <tr>
                    <th style="width: 3rem"></th>
                    <th>{{ 'ExpenseSettlement_Col_Claim' | translateWF: 'Claim' }}</th>
                    <th class="text-right" style="width: 10rem">{{ 'ExpenseSettlement_Col_Total' | translateWF: 'Total' }}</th>
                    <th class="text-right" style="width: 10rem">{{ 'ExpenseSettlement_Col_Outstanding' | translateWF: 'Outstanding' }}</th>
                    <th class="text-right" style="width: 10rem">{{ 'ExpenseSettlement_Col_Applied' | translateWF: 'Applied' }}</th>
                  </tr>
                </ng-template>
                <ng-template pTemplate="body" let-rowData>
                  @let row = getRow(rowData);
                  <tr [formGroup]="row">
                    <td><p-checkbox formControlName="selected" [binary]="true" /></td>
                    <td>
                      <a class="cs-table-link font-mono" (click)="openClaim(row.controls.reference.value)">{{ row.controls.number.value }}</a>
                    </td>
                    <td class="text-right tabular-nums">{{ row.controls.total.value | number: '1.2-2' }}</td>
                    <td class="text-right tabular-nums">{{ row.controls.outstanding.value | number: '1.2-2' }}</td>
                    <td class="text-right tabular-nums font-semibold" [class.text-surface-400]="!row.controls.selected.value">
                      {{ row.controls.appliedAmount.value | number: '1.2-2' }}
                    </td>
                  </tr>
                </ng-template>
                <ng-template pTemplate="emptymessage">
                  <tr>
                    <td colspan="5" class="text-center">{{ 'ExpenseSettlement_Claims_Empty' | translateWF: 'No posted claims are waiting to be settled.' }}</td>
                  </tr>
                </ng-template>
              </p-table>
            </p-card>
            <small class="block mt-3 text-surface-500">
              {{ 'ExpenseSettlement_Applied_Hint' | translateWF: 'Each claim is settled for its full outstanding amount.' }}
            </small>
          </p-panel>

          @if (isTrip()) {
            <p-panel>
              <ng-template pTemplate="header">
                <span class="font-semibold">
                  {{ 'ExpenseSettlement_Section_Advances' | translateWF: 'Advances to net' }}
                  <p-tag class="ml-2" severity="secondary" [value]="selectedAdvances() + ' / ' + advanceRows.length" />
                </span>
              </ng-template>
              <p-card data-variant="compact">
                <p-table [value]="advanceRows.controls" [rowHover]="true">
                  <ng-template pTemplate="header">
                    <tr>
                      <th style="width: 3rem"></th>
                      <th>{{ 'ExpenseSettlement_Col_Advance' | translateWF: 'Advance' }}</th>
                      <th class="text-right" style="width: 10rem">{{ 'ExpenseSettlement_Col_Amount' | translateWF: 'Amount' }}</th>
                      <th class="text-right" style="width: 10rem">{{ 'ExpenseSettlement_Col_Outstanding' | translateWF: 'Outstanding' }}</th>
                      <th class="text-right" style="width: 10rem">{{ 'ExpenseSettlement_Col_Applied' | translateWF: 'Applied' }}</th>
                    </tr>
                  </ng-template>
                  <ng-template pTemplate="body" let-rowData>
                    @let row = getRow(rowData);
                    <tr [formGroup]="row">
                      <td><p-checkbox formControlName="selected" [binary]="true" /></td>
                      <td>
                        <a class="cs-table-link font-mono" (click)="openAdvance(row.controls.reference.value)">{{ row.controls.number.value }}</a>
                      </td>
                      <td class="text-right tabular-nums">{{ row.controls.total.value | number: '1.2-2' }}</td>
                      <td class="text-right tabular-nums">{{ row.controls.outstanding.value | number: '1.2-2' }}</td>
                      <td class="text-right tabular-nums font-semibold" [class.text-surface-400]="!row.controls.selected.value">
                        {{ row.controls.appliedAmount.value | number: '1.2-2' }}
                      </td>
                    </tr>
                  </ng-template>
                  <ng-template pTemplate="emptymessage">
                    <tr>
                      <td colspan="5" class="text-center">{{ 'ExpenseSettlement_Advances_Empty' | translateWF: 'The trip has no posted advances left to net.' }}</td>
                    </tr>
                  </ng-template>
                </p-table>
              </p-card>
              <small class="block mt-3 text-surface-500">
                {{
                  'ExpenseSettlement_Advances_Hint'
                    | translateWF: 'Advances the employee already received net against the claims: if they exceed them, the employee refunds the difference.'
                }}
              </small>
            </p-panel>
          }

          @if (direction() !== directions.None || isPosted()) {
            <app-bank-charges-section
              [title]="
                direction() === directions.Incoming
                  ? ('ExpenseSettlement_Section_BankIn' | translateWF: 'Bank receiving the refund, reference, charges & documents')
                  : ('ExpenseSettlement_Section_Bank' | translateWF: 'Bank, reference, charges & documents')
              "
              [bankAccountControl]="form.controls.bankAccount"
              [referenceControl]="form.controls.bankingReference"
              [bankingFeeControl]="form.controls.bankingFee"
              [securityFeeControl]="form.controls.securityFee"
              [bankAccountOptions]="bankOptions()"
              [noBankHint]="
                'FundOpening_NoBank_Hint'
                  | translateWF: 'No active bank account in this currency allows Petty Cash. Accounting enables it on the bank’s GL account (posting module Caja Chica).'
              "
              [currencyLabel]="currencyCode()"
              [attachments]="current.attachments"
              [canAttach]="canSettle"
              [bankingFeeHint]="
                direction() === directions.Incoming
                  ? ('ExpenseSettlement_FeeIn_Hint' | translateWF: 'Charged on arrival — deducted from the refund.')
                  : ('ApAdvance_BankingFee_Hint' | translateWF: 'Wire / transfer charge — added to the total paid.')
              "
              [securityFeeHint]="
                direction() === directions.Incoming
                  ? ('ExpenseSettlement_FeeIn_Hint' | translateWF: 'Charged on arrival — deducted from the refund.')
                  : ('ApAdvance_SecurityFee_Hint' | translateWF: 'Insurance / handling — added to the total paid.')
              "
              (fileSelected)="onFile($event)"
              (attachmentOpened)="openAttachment($event)"
            />
          } @else {
            <p-message severity="info">
              {{ 'ExpenseSettlement_NoCash_Hint' | translateWF: 'The claims and advances cancel out: posting settles them without a bank movement.' }}
            </p-message>
          }
        </div>

        <div class="span-12-12-4 flex flex-col gap-5 sticky-summary">
          <app-settlement-summary
            [title]="'ExpenseSettlement_Section_Summary' | translateWF: 'Settlement summary'"
            [hint]="current.ownerName"
            [baseLabel]="'ExpenseSettlement_Summary_Claims' | translateWF: 'Claims settled'"
            [baseAmount]="claimsTotal()"
            [extraRows]="summaryRows()"
            [bankingFeeLabel]="'BankCharges_BankingFee' | translateWF: 'Banking fee'"
            [securityFeeLabel]="'BankCharges_SecurityFee' | translateWF: 'Security fee'"
            [bankingFee]="direction() === directions.None ? 0 : (form.controls.bankingFee.value ?? 0)"
            [securityFee]="direction() === directions.None ? 0 : (form.controls.securityFee.value ?? 0)"
            [feesReduceNet]="direction() === directions.Incoming"
            [netLabel]="summaryNetLabel()"
            [net]="bankAmount()"
            [currencyLabel]="currencyCode()"
          />

          @if (!isPosted() && preview(); as entry) {
            <p-panel [header]="'ExpenseClaim_Section_Journal' | translateWF: 'Journal entry to post'">
              <p-card data-variant="compact">
                @if (entry.missing.length > 0) {
                  <p-message severity="warn" styleClass="mb-2">{{ entry.missing.join('; ') }}</p-message>
                }
                <p-table [value]="entry.lines" [rowHover]="true">
                  <ng-template pTemplate="header">
                    <tr>
                      <th>{{ 'ExpenseClaim_Journal_Account' | translateWF: 'Account' }}</th>
                      <th class="text-right" style="width: 7rem">{{ 'ExpenseClaim_Journal_Debit' | translateWF: 'Debit' }}</th>
                      <th class="text-right" style="width: 7rem">{{ 'ExpenseClaim_Journal_Credit' | translateWF: 'Credit' }}</th>
                    </tr>
                  </ng-template>
                  <ng-template pTemplate="body" let-rowData>
                    @let line = getJournalRow(rowData);
                    <tr>
                      <td>
                        <span class="font-mono">{{ line.accountNumber }}</span>
                        <small class="block">{{ line.accountName }}</small>
                        <small class="block text-surface-500">{{ line.description }}</small>
                      </td>
                      <td class="text-right tabular-nums">{{ line.debit ? (line.debit | number: '1.2-2') : '' }}</td>
                      <td class="text-right tabular-nums">{{ line.credit ? (line.credit | number: '1.2-2') : '' }}</td>
                    </tr>
                  </ng-template>
                  <ng-template pTemplate="footer">
                    <tr>
                      <td class="text-right font-semibold">{{ 'ExpenseClaim_Journal_Totals' | translateWF: 'Totals' }} ({{ symbol() }})</td>
                      <td class="text-right tabular-nums font-semibold">{{ entry.totalDebit | number: '1.2-2' }}</td>
                      <td class="text-right tabular-nums font-semibold">{{ entry.totalCredit | number: '1.2-2' }}</td>
                    </tr>
                  </ng-template>
                </p-table>
              </p-card>
              <small class="block mt-3 text-surface-500">
                {{ 'ExpenseSettlement_Preview_Hint' | translateWF: 'As last saved. Save to refresh it after changing claims, advances or charges.' }}
              </small>
            </p-panel>
          }

          @if (isPosted()) {
            <p-panel [header]="'ExpenseClaim_Section_Posted' | translateWF: 'Posted'">
              <p-card>
                <div class="flex flex-col gap-2 mb-3 text-sm">
                  <span>{{ 'ExpenseSettlement_PostedOn' | translateWF: 'Posted on' }} {{ current.settlementDate | date: 'yyyy-MM-dd' : 'UTC' }}</span>
                  @if (current.bankTransactionNumber) {
                    <span
                      >{{ 'FundOpening_BankMovement' | translateWF: 'Bank movement' }}: <span class="font-mono">{{ current.bankTransactionNumber }}</span></span
                    >
                  }
                  @if (current.functionalAmount !== null) {
                    <span class="text-surface-500">
                      {{ 'ExpenseSettlement_Functional' | translateWF: 'In the books' }}: {{ current.functionalAmount | number: '1.2-2' }} · USD
                      {{ current.amountUsd | number: '1.2-2' }}
                    </span>
                  }
                </div>
                <app-journal-entry-summary [references]="current.journalEntryReference" [currencyLabel]="currencyCode()" />
              </p-card>
            </p-panel>
          }
        </div>
      </div>
    }
  </div>
</main>
